// src/components/TrackManagement.js
import React, { useState, useEffect } from 'react';
import { db } from '../firebaseConfig';
import {
  collection,
  query,
  where,
  onSnapshot,
  addDoc,
  deleteDoc,
  doc,
  orderBy,
  updateDoc 
} from 'firebase/firestore';
import { useTranslation } from 'react-i18next'; // <-- MODIFICA: Importa hook

// --- IMPORT MUI (Invariati) ---
import {
  Box, Typography, IconButton, CircularProgress, Paper, Button, TextField,
  List, ListItem, ListItemText, ListItemIcon,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Collapse, Alert, Divider, Autocomplete, Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import EditIcon from '@mui/icons-material/Edit';
import SpeedIcon from '@mui/icons-material/Speed';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SyncIcon from '@mui/icons-material/Sync';

import TrackMapEditor from './race_engineer/TrackMapEditor';
import { getReTrack, saveReTrackSectors, trackNameToSlug, upsertReTrackFromAnalysis, initReTrackFromDatabase } from '../utils/reTracksService';
import { parseCsv, getCircuits, getCircuitById } from '../utils/raceEngineerApi';

function TrackManagement({ teamId }) {
  // --- MODIFICA: Inizializza t() ---
  const { t } = useTranslation('track');
  // --- FINE MODIFICA ---

  // Stati (invariati)
  const [tracks, setTracks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [newTrackName, setNewTrackName] = useState('');
  const [newTrackMeters, setNewTrackMeters] = useState('');
  const [newTrackAddress, setNewTrackAddress] = useState('');
  const [newTrackContacts, setNewTrackContacts] = useState('');
  const [newTrackCorners, setNewTrackCorners] = useState('');
  const [newTrackLat, setNewTrackLat]           = useState(null);
  const [newTrackLon, setNewTrackLon]           = useState(null);
  const [selectedCircuitId, setSelectedCircuitId] = useState(null);

  // Database circuiti (caricato una volta)
  const [aimCircuits, setAimCircuits] = useState([]);
  const [aimLoading, setAimLoading]   = useState(false);

  // Carica database circuiti al mount
  React.useEffect(() => {
    setAimLoading(true);
    getCircuits()
      .then(data => { setAimCircuits(data); setAimLoading(false); })
      .catch(() => setAimLoading(false));
  }, []);

  // Stati Modale Modifica
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [currentTrack, setCurrentTrack] = useState(null);

  // Stati Race Engineer dialog
  const [reDialogTrack, setReDialogTrack]   = useState(null);
  const [reTrackData, setReTrackData]       = useState(null);
  const [reTrackLoading, setReTrackLoading] = useState(false);
  const [syncLoading, setSyncLoading]       = useState(false);
  const [syncMsg, setSyncMsg]               = useState(null); // {type, text}
  // Caricamento CSV nel dialog
  const [uploadOpen, setUploadOpen]         = useState(false);
  const [uploadCsv, setUploadCsv]           = useState(null);
  const [uploadSplit, setUploadSplit]       = useState(null);
  const [uploadLoading, setUploadLoading]   = useState(false);
  const [uploadMsg, setUploadMsg]           = useState(null); // {type, text}
  const [editFormData, setEditFormData] = useState({
    nome: '',
    lengthMeters: '',
    address: '',
    contacts: '',
    corners: '' // Curve separate da virgola
  });

  // useEffect (invariato)
  useEffect(() => {
    if (!teamId) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    
    const tracksQuery = query(
      collection(db, "tracks"),
      where("teamId", "==", teamId),
      orderBy("nome", "asc")
    );

    const unsubscribe = onSnapshot(tracksQuery, (querySnapshot) => {
      const tracksList = [];
      querySnapshot.forEach((doc) => {
        tracksList.push({ id: doc.id, ...doc.data() });
      });
      setTracks(tracksList);
      setIsLoading(false);
    }, (error) => {
      console.error("Errore listener piste (serve indice?):", error);
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [teamId]);

  // --- MODIFICA: handleAddTrack (con t()) ---
  const handleAddTrack = async (e) => {
    e.preventDefault();
    const trackName = newTrackName.trim();
    if (!trackName) return;

    if (tracks.some(track => track.nome.toLowerCase() === trackName.toLowerCase())) {
      alert(t('exists_error')); // Tradotto
      return;
    }

    setIsSubmitting(true);
    try {
      const cornersArray = newTrackCorners.split(',').map(c => c.trim()).filter(c => c);
      const trackDoc = {
        teamId: teamId,
        nome: trackName,
        lengthMeters: Number(newTrackMeters) || null,
        address: newTrackAddress.trim(),
        contacts: newTrackContacts.trim(),
        corners: cornersArray,
      };
      if (newTrackLat && newTrackLon) {
        trackDoc.lat = newTrackLat;
        trackDoc.lon = newTrackLon;
      }
      if (selectedCircuitId) {
        trackDoc.circuitDbId = selectedCircuitId; // salva per la sincronizzazione futura
      }
      await addDoc(collection(db, "tracks"), trackDoc);
      setNewTrackName('');
      setNewTrackMeters('');
      setNewTrackAddress('');
      setNewTrackContacts('');
      setNewTrackCorners('');
      setNewTrackLat(null);
      setNewTrackLon(null);
      // Popola re_tracks in background dal database circuiti (mappa subito disponibile)
      if (selectedCircuitId) {
        const cid = selectedCircuitId;
        setSelectedCircuitId(null);
        getCircuitById(cid).then(circuitData => {
          if (circuitData?.gps_trace?.length) {
            const slug = trackNameToSlug(trackName);
            initReTrackFromDatabase(slug, trackName, circuitData).catch(() => {});
          }
        }).catch(() => {});
      }
    } catch (error) {
      console.error("Errore aggiunta pista:", error);
      alert(t('add_error')); // Tradotto
    }
    setIsSubmitting(false);
  };

  // --- MODIFICA: handleDeleteTrack (con t()) ---
  const handleDeleteTrack = async (trackId) => {
    if (window.confirm(t('delete_confirm'))) { // Tradotto
      try {
        await deleteDoc(doc(db, "tracks", trackId));
      } catch (error) {
        console.error("Errore eliminazione pista:", error);
        alert(t('delete_error')); // Tradotto
      }
    }
  };

  // Funzioni Modale Modifica (invariate)
  const handleOpenEditModal = (track) => {
    setCurrentTrack(track); 
    setEditFormData({
      nome: track.nome || '',
      lengthMeters: track.lengthMeters || '',
      address: track.address || '',
      contacts: track.contacts || '',
      corners: track.corners ? track.corners.join(', ') : ''
    });
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setCurrentTrack(null);
  };

  const handleOpenReDialog = async (track) => {
    setReDialogTrack(track);
    setReTrackData(null);
    setReTrackLoading(true);
    try {
      const slug = trackNameToSlug(track.nome);
      const data = await getReTrack(slug);
      setReTrackData(data);
    } catch (e) {
      console.error('Errore caricamento re_track:', e);
    } finally {
      setReTrackLoading(false);
    }
  };

  const handleCloseReDialog = () => {
    setReDialogTrack(null);
    setReTrackData(null);
    setUploadOpen(false);
    setUploadCsv(null);
    setUploadSplit(null);
    setUploadMsg(null);
  };

  const handleUploadTrackData = async () => {
    if (!uploadCsv || !reDialogTrack) return;
    setUploadLoading(true);
    setUploadMsg(null);
    try {
      const result = await parseCsv({ csv: uploadCsv, splitReport: uploadSplit });
      const slug   = trackNameToSlug(reDialogTrack.nome);
      await upsertReTrackFromAnalysis(slug, reDialogTrack.nome, result.track_data);

      // Salva lat/lon centroide nel documento tracks per il widget meteo
      const gps = result.track_data?.track_gps;
      if (gps?.length > 0 && reDialogTrack.id) {
        const lat = gps.reduce((s, p) => s + p.lat, 0) / gps.length;
        const lon = gps.reduce((s, p) => s + p.lon, 0) / gps.length;
        await updateDoc(doc(db, 'tracks', reDialogTrack.id), {
          lat: Math.round(lat * 100000) / 100000,
          lon: Math.round(lon * 100000) / 100000,
        });
      }

      // Ricarica i dati aggiornati
      const updated = await getReTrack(slug);
      setReTrackData(updated);
      setUploadMsg({ type: 'success', text: t('re_update_success', { sectors: updated?.sectors?.length ?? 0, gps: updated?.gps_trace?.length ?? 0 }) });
      setUploadCsv(null);
      setUploadSplit(null);
      setUploadOpen(false);
    } catch (e) {
      setUploadMsg({ type: 'error', text: t('re_update_error', { message: e.message || e }) });
    } finally {
      setUploadLoading(false);
    }
  };

  // Sincronizza GPS dal database circuiti (forza sovrascrittura)
  const handleSyncFromDb = async () => {
    if (!reDialogTrack?.circuitDbId) return;
    setSyncLoading(true);
    setSyncMsg(null);
    try {
      const circuitData = await getCircuitById(reDialogTrack.circuitDbId);
      if (!circuitData?.gps_trace?.length) {
        setSyncMsg({ type: 'error', text: t('sync_gps_error_no_data') });
        return;
      }
      const slug = trackNameToSlug(reDialogTrack.nome);
      await initReTrackFromDatabase(slug, reDialogTrack.nome, circuitData);
      const updated = await getReTrack(slug);
      setReTrackData(updated);
      setSyncMsg({ type: 'success', text: t('sync_gps_success', { count: updated?.gps_trace?.length ?? 0 }) });
    } catch (e) {
      setSyncMsg({ type: 'error', text: t('sync_gps_error', { message: e.message || e }) });
    } finally {
      setSyncLoading(false);
    }
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({ ...prev, [name]: value }));
  };

  // --- MODIFICA: handleUpdateTrack (con t()) ---
  const handleUpdateTrack = async (e) => {
    e.preventDefault();
    if (!currentTrack || !editFormData.nome.trim()) return;

    setIsSubmitting(true);
    const docRef = doc(db, "tracks", currentTrack.id);
    try {
      const cornersArray = editFormData.corners.split(',').map(c => c.trim()).filter(c => c);
      await updateDoc(docRef, {
        nome: editFormData.nome.trim(),
        lengthMeters: Number(editFormData.lengthMeters) || null,
        address: editFormData.address.trim(),
        contacts: editFormData.contacts.trim(),
        corners: cornersArray
      });
      handleCloseEditModal(); 
    } catch (error) {
      console.error("Errore aggiornamento pista:", error);
      alert(t('edit_error')); // Tradotto
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- RENDER (MODIFICATO con t()) ---
  return (
    <>
      {/* 1. Header */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, backgroundColor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
          {t('title')} {/* Tradotto */}
        </Typography>
      </Paper>
      
      {/* 2. Paper Form Aggiungi Pista */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: { xs: 2, sm: 3 }, 
          mb: 3,
          border: '1px solid', 
          borderColor: 'divider', 
          backgroundColor: 'background.paper' 
        }}
      >
        <Box component="form" onSubmit={handleAddTrack} sx={{ mb: 3 }}>
          <Typography variant="h5" gutterBottom>{t('add_title')}</Typography>

          {/* ── Cerca circuito ── */}
          <Box sx={{ mb: 2.5 }}>
            <Autocomplete
              options={aimCircuits}
              loading={aimLoading}
              getOptionLabel={(o) => o.layout ? `${o.name} [${o.layout}]` : o.name}
              filterOptions={(options, { inputValue }) => {
                if (inputValue.length < 2) return [];
                const q = inputValue.toLowerCase();
                return options.filter(o =>
                  o.name.toLowerCase().includes(q) ||
                  (o.layout && o.layout.toLowerCase().includes(q))
                ).slice(0, 60);
              }}
              onChange={(_, val) => {
                if (!val) { setNewTrackLat(null); setNewTrackLon(null); setSelectedCircuitId(null); return; }
                const name = val.layout ? `${val.name} [${val.layout}]` : val.name;
                setNewTrackName(name);
                setNewTrackLat(val.lat);
                setNewTrackLon(val.lon);
                setSelectedCircuitId(val.id);
              }}
              renderOption={(props, o) => (
                <li {...props} key={o.id}>
                  <Box>
                    <Typography variant="body2">{o.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {o.layout && <><strong>{o.layout}</strong> · </>}{o.country} · {o.lat?.toFixed(3)}, {o.lon?.toFixed(3)}
                    </Typography>
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Cerca circuito (digita almeno 2 caratteri)"
                  placeholder="Es: Monza, Varano, Imola..."
                  InputProps={{ ...params.InputProps, startAdornment: <SearchIcon sx={{ ml: 1, mr: 0.5, color: 'text.secondary', fontSize: 20 }} /> }}
                />
              )}
              noOptionsText={aimLoading ? 'Caricamento...' : 'Nessun circuito trovato — inserisci il nome manualmente'}
              clearOnBlur={false}
            />
            {newTrackLat && (
              <Chip size="small" label={`GPS pronto: ${newTrackLat.toFixed(4)}, ${newTrackLon.toFixed(4)}`}
                color="success" variant="outlined" sx={{ mt: 0.75 }} onDelete={() => { setNewTrackLat(null); setNewTrackLon(null); }} />
            )}
          </Box>

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2.5, mb: 2.5 }}>
            <TextField
              label={t('name_label')}
              value={newTrackName}
              onChange={(e) => setNewTrackName(e.target.value)}
              required
              disabled={isSubmitting}
              fullWidth
              sx={{ flex: 2 }} 
            />
             <TextField
              label={t('length_label')}
              type="number"
              value={newTrackMeters}
              onChange={(e) => setNewTrackMeters(e.target.value)}
              disabled={isSubmitting}
              fullWidth
              sx={{ flex: 1 }}
            />
          </Box>
          
          <Box sx={{ display: 'flex', mb: 2.5 }}>
             <TextField label={t('address_label')} value={newTrackAddress} onChange={(e) => setNewTrackAddress(e.target.value)} disabled={isSubmitting} fullWidth />
          </Box>
          <Box sx={{ display: 'flex', mb: 2.5 }}>
            <TextField label={t('contacts_label')} value={newTrackContacts} onChange={(e) => setNewTrackContacts(e.target.value)} disabled={isSubmitting} fullWidth multiline rows={2} />
          </Box>
          <Box sx={{ display: 'flex', mb: 2.5 }}>
            <TextField label={t('corners_label', { defaultValue: 'Elenco Curve (Separate da virgola)' })} value={newTrackCorners} onChange={(e) => setNewTrackCorners(e.target.value)} disabled={isSubmitting} fullWidth multiline rows={2} placeholder="Es: Curva 1, Curva 2, Variante Ascari, Parabolica" helperText={t('corners_helper', { defaultValue: 'Verranno auto-generate nella dashboard del pilota per i feedback.' })} />
          </Box>
          
          <Button type="submit" variant="contained" disabled={isSubmitting || !newTrackName} startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <AddIcon />} sx={{ mt: 1, height: '56px' }} size="large" >
            {t('add_button')}
          </Button>
        </Box>
      </Paper>
      
      {/* 3. Paper Lista Piste */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: { xs: 2, sm: 3 },
          border: '1px solid', 
          borderColor: 'divider', 
          backgroundColor: 'background.paper' 
        }}
      >
        <Typography variant="h5" gutterBottom>{t('saved_title')}</Typography>
        {isLoading && <CircularProgress size={24} />}
        {!isLoading && tracks.length === 0 && ( <Typography variant="body2" sx={{ fontStyle: 'italic' }}> {t('empty')} </Typography> )}

        {!isLoading && tracks.length > 0 && (
          <List dense>
            {tracks.map((track) => (
              <ListItem
                key={track.id}
                divider
                sx={{ 
                  backgroundColor: 'background.default',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  mb: 1
                }}
                secondaryAction={
                  <Box>
                    <IconButton
                      edge="end"
                      aria-label="race engineer"
                      onClick={() => handleOpenReDialog(track)}
                      sx={{ mr: 0.5, color: 'secondary.main' }}
                      title="Configura Race Engineer"
                    >
                      <SpeedIcon />
                    </IconButton>
                    <IconButton edge="end" aria-label="edit" onClick={() => handleOpenEditModal(track)} sx={{ mr: 0.5 }}>
                      <EditIcon />
                    </IconButton>
                    <IconButton edge="end" aria-label="delete" onClick={() => handleDeleteTrack(track.id)}>
                      <DeleteIcon color="error" />
                    </IconButton>
                  </Box>
                }
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <TrackChangesIcon />
                </ListItemIcon>
                <ListItemText 
                  primary={`${track.nome} ${track.lengthMeters ? `(${track.lengthMeters} m)` : ''}`} 
                  primaryTypographyProps={{ fontWeight: 600, fontSize: '1.1rem' }}
                  secondary={
                    <>
                      <Typography component="span" variant="body2" color="text.secondary" sx={{ display: 'block' }}>
                        {t('address_prefix')} {track.address || 'N/A'}
                      </Typography>
                      <Typography component="span" variant="body2" color="text.secondary" sx={{ display: 'block' }}>
                        {t('contacts_prefix')} {track.contacts || 'N/A'}
                      </Typography>
                      {track.corners && track.corners.length > 0 && (
                         <Typography component="span" variant="body2" color="secondary.main" sx={{ display: 'block', mt: 0.5 }}>
                           {track.corners.length} Curve preimpostate
                         </Typography>
                      )}
                    </>
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
      </Paper>

      {/* Race Engineer Dialog */}
      <Dialog
        open={!!reDialogTrack}
        onClose={handleCloseReDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { minHeight: '70vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, fontWeight: 700 }}>
          <SpeedIcon sx={{ color: 'secondary.main' }} />
          Race Engineer — {reDialogTrack?.nome}
        </DialogTitle>
        <DialogContent>
          {/* ── Pannello carica/aggiorna dati GPS ── */}
          <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', mb: 2, overflow: 'hidden' }}>
            {/* Header collassabile */}
            <Box
              onClick={() => setUploadOpen(v => !v)}
              sx={{ px: 2, py: 1.25, display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer',
                bgcolor: 'background.paper', '&:hover': { bgcolor: 'rgba(255,255,255,0.02)' }, userSelect: 'none' }}>
              <SyncIcon sx={{ fontSize: 18, color: 'secondary.main' }} />
              <Typography variant="subtitle2" fontWeight={700}>{t('re_upload_title')}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                · {t('re_upload_subtitle')}
              </Typography>
              <ExpandMoreIcon sx={{ ml: 'auto', color: 'text.secondary', fontSize: 20,
                transform: uploadOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
            </Box>

            <Collapse in={uploadOpen}>
              <Divider />
              <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                  {/* CSV principale */}
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      {t('re_csv_label')}
                    </Typography>
                    <Box
                      onClick={() => document.getElementById('tm-csv').click()}
                      sx={{ border: '2px dashed', borderColor: uploadCsv ? 'success.main' : 'divider',
                        borderRadius: 2, p: 2, textAlign: 'center', cursor: 'pointer', minHeight: 72,
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.5,
                        '&:hover': { borderColor: 'secondary.main' } }}>
                      <input id="tm-csv" type="file" accept=".csv" style={{ display: 'none' }}
                        onChange={e => setUploadCsv(e.target.files[0] || null)} />
                      {uploadCsv
                        ? <><CheckCircleOutlineIcon sx={{ color: 'success.main', fontSize: 18 }} /><Typography variant="caption" fontWeight={600}>{uploadCsv.name}</Typography></>
                        : <><UploadFileIcon sx={{ fontSize: 18, color: 'secondary.main' }} /><Typography variant="caption">CSV Telemetria</Typography></>
                      }
                    </Box>
                  </Box>

                  {/* Split report */}
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      {t('re_split_label')}
                    </Typography>
                    <Box
                      onClick={() => document.getElementById('tm-split').click()}
                      sx={{ border: '2px dashed', borderColor: uploadSplit ? 'success.main' : 'divider',
                        borderRadius: 2, p: 2, textAlign: 'center', cursor: 'pointer', minHeight: 72,
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.5,
                        '&:hover': { borderColor: 'secondary.main' } }}>
                      <input id="tm-split" type="file" accept=".csv" style={{ display: 'none' }}
                        onChange={e => setUploadSplit(e.target.files[0] || null)} />
                      {uploadSplit
                        ? <><CheckCircleOutlineIcon sx={{ color: 'success.main', fontSize: 18 }} /><Typography variant="caption" fontWeight={600}>{uploadSplit.name}</Typography></>
                        : <><UploadFileIcon sx={{ fontSize: 18, color: 'text.disabled' }} /><Typography variant="caption" color="text.secondary">Split Report</Typography></>
                      }
                    </Box>
                  </Box>
                </Box>

                {uploadMsg && (
                  <Alert severity={uploadMsg.type} onClose={() => setUploadMsg(null)} sx={{ py: 0.5 }}>
                    {uploadMsg.text}
                  </Alert>
                )}

                <Button
                  variant="contained" color="secondary"
                  disabled={!uploadCsv || uploadLoading}
                  onClick={handleUploadTrackData}
                  startIcon={uploadLoading ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
                >
                  {uploadLoading ? t('re_updating') : t('re_update_button')}
                </Button>
              </Box>
            </Collapse>
          </Paper>

          {/* ── Sincronizza GPS dal database circuiti ── */}
          {reDialogTrack?.circuitDbId && (
            <Box sx={{ mb: 2 }}>
              {syncMsg && (
                <Alert severity={syncMsg.type} onClose={() => setSyncMsg(null)} sx={{ mb: 1, py: 0.5 }}>
                  {syncMsg.text}
                </Alert>
              )}
              <Button
                variant="outlined"
                color="info"
                size="small"
                fullWidth
                disabled={syncLoading}
                onClick={handleSyncFromDb}
                startIcon={syncLoading ? <CircularProgress size={14} color="inherit" /> : <SyncIcon />}
              >
                {syncLoading ? t('sync_gps_loading') : t('sync_gps_button')}
              </Button>
            </Box>
          )}

          {/* ── TrackMapEditor ── */}
          {reTrackLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress color="secondary" />
            </Box>
          ) : (
            <TrackMapEditor
              key={reTrackData?.contributions ?? 'empty'}
              trackSlug={reDialogTrack ? trackNameToSlug(reDialogTrack.nome) : null}
              reTrack={reTrackData}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseReDialog}>{t('close')}</Button>
        </DialogActions>
      </Dialog>

      {/* 4. Modale Modifica */}
      <Dialog open={isEditModalOpen} onClose={handleCloseEditModal} maxWidth="md" fullWidth>
        <DialogTitle>{t('edit_title')}</DialogTitle>
        <Box component="form" onSubmit={handleUpdateTrack}>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2.5, mb: 2.5, mt: 1 }}>
              <TextField
                name="nome" 
                label={t('name_label')}
                value={editFormData.nome}
                onChange={handleEditFormChange}
                required
                disabled={isSubmitting}
                fullWidth
                sx={{ flex: 2 }} 
              />
              <TextField
                name="lengthMeters"
                label={t('length_label')}
                type="number"
                value={editFormData.lengthMeters}
                onChange={handleEditFormChange}
                disabled={isSubmitting}
                fullWidth
                sx={{ flex: 1 }}
              />
            </Box>
            <Box sx={{ display: 'flex', mb: 2.5 }}>
              <TextField
                name="address"
                label={t('address_label')}
                value={editFormData.address}
                onChange={handleEditFormChange}
                disabled={isSubmitting}
                fullWidth
              />
            </Box>
            <Box sx={{ display: 'flex', mb: 2.5 }}>
              <TextField
                name="contacts"
                label={t('contacts_label')}
                value={editFormData.contacts}
                onChange={handleEditFormChange}
                disabled={isSubmitting}
                fullWidth
                multiline
                rows={2} 
              />
            </Box>
            <Box sx={{ display: 'flex', mb: 2.5 }}>
              <TextField
                name="corners"
                label={t('corners_label', { defaultValue: 'Elenco Curve (Separate da virgola)' })}
                value={editFormData.corners}
                onChange={handleEditFormChange}
                disabled={isSubmitting}
                fullWidth
                multiline
                rows={2}
                placeholder="Es: Curva 1, Curva 2, Variante Ascari, Parabolica"
                helperText={t('corners_helper', { defaultValue: 'Verranno auto-generate nella dashboard del pilota per i feedback.' })}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 0 }}>
            <Button onClick={handleCloseEditModal} disabled={isSubmitting}>
              {t('edit_cancel_button')}
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              disabled={isSubmitting || !editFormData.nome}
            >
              {isSubmitting ? <CircularProgress size={24} /> : t('edit_save_button')}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </>
  );
}

export default TrackManagement;